import { useCallback, useEffect, useState } from "react";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { Alert, CircularProgress, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LmsCourseCard, {
  type LmsCourseCardData,
} from "@/components/lms/lmsCourseCard";
import {
  loadPaginatedEntity,
  mapLearnerEnrolmentToCourseCardData,
} from "@/components/lms/lmsCoursesShared";
import {
  createPromiseRequest,
  getLmsCoursesRoute,
  getLmsLearnerEnrolmentOverviewRoute,
  getLmsScormLaunchRoute,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import { setBackgroundTask } from "@/components/state/backgroundTasks/backgroundTasksSlice";
import { AppDispatch, RootState } from "@/components/state/store";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import HighFiveIcon from "@/icons/highFiveIcon";
import i18n from "@/translations/i18n";
import styles from "@/routes/lms/lmsScormCompletion.module.css";

const SUGGESTION_LIMIT = 3;

export default function LmsScormCompletion() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const loginState = useSelector((state: RootState) => state.login);
  const [suggestedCourses, setSuggestedCourses] = useState<LmsCourseCardData[]>(
    []
  );
  const [remainingCoursesCount, setRemainingCoursesCount] = useState(0);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(true);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [completionLoadFailed, setCompletionLoadFailed] = useState(false);
  const [suggestionsLoadFailed, setSuggestionsLoadFailed] = useState(false);
  const [redirectToOverview, setRedirectToOverview] = useState(false);
  const [synchronizedLanguage, setSynchronizedLanguage] = useState<
    string | null
  >(null);
  const [certificateReady, setCertificateReady] = useState(false);

  usePageHeading(t("scorm_completion_page_title"));

  useEffect(() => {
    const taskTrackingId = new URLSearchParams(location.search).get("task_tracking_id");
    if (!taskTrackingId) return;
    dispatch(setBackgroundTask({ id: taskTrackingId, description: t("lms_download_certificate"), kind: "certificate" }));
  }, [dispatch, location.search, t]);

  useEffect(() => {
    const taskTrackingId = new URLSearchParams(location.search).get("task_tracking_id");
    if (!taskTrackingId) return;
    const refreshCompletion = (event: Event) => {
      if ((event as CustomEvent<{ taskId: string }>).detail.taskId === taskTrackingId) setCertificateReady(true);
    };
    window.addEventListener("background-task-completed", refreshCompletion);
    return () => window.removeEventListener("background-task-completed", refreshCompletion);
  }, [location.search]);

  useEffect(() => {
    let isCurrentLanguage = true;
    const preferredLanguage = loginState.preferred_language ?? "";
    const synchronizeLanguage = async () => {
      if (preferredLanguage) await i18n.changeLanguage(preferredLanguage);
      if (isCurrentLanguage) setSynchronizedLanguage(preferredLanguage);
    };

    void synchronizeLanguage();
    return () => {
      isCurrentLanguage = false;
    };
  }, [loginState.preferred_language]);

  const loadSuggestions = useCallback(async () => {
    if (!loginState.subjectId) {
      setSuggestionsLoadFailed(true);
      return;
    }

    setIsLoadingSuggestions(true);
    setSuggestionsLoadFailed(false);
    try {
      const response = await loadPaginatedEntity("enrolment", {
        participant_id: loginState.subjectId,
        status__in: "not_started,in_progress",
        depth: 1,
        ordering: "status,-progress_percentage,created_on",
        limit: SUGGESTION_LIMIT,
      });
      setRemainingCoursesCount(response.count);
      setSuggestedCourses(
        response.results.map(
          (enrolment) =>
            mapLearnerEnrolmentToCourseCardData(enrolment, t).course
        )
      );
    } catch {
      setSuggestionsLoadFailed(true);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [loginState.subjectId, t]);

  useEffect(() => {
    if (synchronizedLanguage !== (loginState.preferred_language ?? "")) return;
    if (!enrolmentId) {
      setCompletionLoadFailed(true);
      setIsLoadingCompletion(false);
      return;
    }

    let isCurrentRequest = true;
    const loadCompletion = async () => {
      setIsLoadingCompletion(true);
      setCompletionLoadFailed(false);
      try {
        const response = await createPromiseRequest({
          entity: "enrolment",
          entityId: enrolmentId,
          method: "get",
          parametersToRender: { depth: 1 },
          disableGlobalSpinner: true,
        });
        if (!isCurrentRequest) return;

        const enrolment = response?.data;
        if (String(enrolment?.status ?? "").toLowerCase() !== "completed") {
          setRedirectToOverview(true);
          return;
        }

        void loadSuggestions();
      } catch {
        if (isCurrentRequest) setCompletionLoadFailed(true);
      } finally {
        if (isCurrentRequest) setIsLoadingCompletion(false);
      }
    };

    void loadCompletion();
    return () => {
      isCurrentRequest = false;
    };
  }, [
    enrolmentId,
    loadSuggestions,
    loginState.preferred_language,
    synchronizedLanguage,
  ]);

  const subdomain = window.location.hostname.split(".")[0];
  if (
    subdomain !== "portal" ||
    shouldBlockLmsForSubdomain({
      subdomain,
      permissions: loginState.permissions,
    })
  ) {
    return <Navigate to="/" replace />;
  }

  if (redirectToOverview && enrolmentId) {
    return (
      <Navigate to={getLmsLearnerEnrolmentOverviewRoute(enrolmentId)} replace />
    );
  }

  const handleStartCourse = (course: LmsCourseCardData) => {
    if (!course.enrolmentId) return;
    navigate(getLmsScormLaunchRoute(course.enrolmentId), { replace: true });
  };

  const handleViewAllCourses = () => {
    navigate(getLmsCoursesRoute("learner"));
  };

  if (isLoadingCompletion) {
    return (
      <main className={styles.page} data-testid="lms-scorm-completion-loading">
        <CircularProgress color="secondary" />
      </main>
    );
  }

  if (completionLoadFailed) {
    return (
      <main className={styles.page} data-testid="lms-scorm-completion-error">
        <section className={styles.errorCard} role="alert">
          <h1>{t("scorm_completion_load_error")}</h1>
          <GenericButton onClick={handleViewAllCourses}>
            {t("scorm_completion_view_all_courses")}
          </GenericButton>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page} data-testid="lms-scorm-completion">
      <section className={styles.content}>
        <div className={styles.hero}>
          <HighFiveIcon width="132" height="132" />
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>{t("scorm_completion_eyebrow")}</p>
            <h1>{t("scorm_completion_heading")}</h1>
            <p className={styles.closeGuidance}>
              {t("scorm_completion_close_tab")}
            </p>
          </div>
        </div>
        {new URLSearchParams(location.search).get("task_tracking_id") && (
          <Alert severity={certificateReady ? "success" : "info"}>
            {t(certificateReady ? "certificate_ready" : "certificate_generating")}
          </Alert>
        )}

        <section className={styles.nextCourses}>
          {isLoadingSuggestions ? (
            <div className={styles.suggestionsLoading}>
              <CircularProgress color="secondary" size={32} />
            </div>
          ) : suggestionsLoadFailed ? (
            <div className={styles.suggestionsError} role="alert">
              <p>{t("scorm_completion_suggestions_error")}</p>
              <div className={styles.errorActions}>
                <GenericButton onClick={() => void loadSuggestions()}>
                  {t("scorm_completion_retry")}
                </GenericButton>
                <GenericButton
                  variant="outlined"
                  onClick={handleViewAllCourses}
                >
                  {t("scorm_completion_view_all_courses")}
                </GenericButton>
              </div>
            </div>
          ) : suggestedCourses.length > 0 ? (
            <>
              <div className={styles.sectionHeading}>
                <div>
                  <h2>{t("scorm_completion_next_courses")}</h2>
                  <p>
                    {t("scorm_completion_remaining_courses", {
                      count: remainingCoursesCount,
                    })}
                  </p>
                </div>
                <GenericButton
                  variant="outlined"
                  onClick={handleViewAllCourses}
                >
                  {t("scorm_completion_view_all_courses")}
                </GenericButton>
              </div>
              <Grid container spacing={2}>
                {suggestedCourses.map((course) => (
                  <Grid item xs={12} md={6} lg={4} key={course.id}>
                    <LmsCourseCard
                      course={course}
                      primaryAction={{
                        label: t(
                          course.bottomStatusTone === "warning"
                            ? "continue_course"
                            : "start_course"
                        ),
                        startIcon: <PlayArrowOutlinedIcon />,
                        onClick: () => handleStartCourse(course),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <div className={styles.allDone}>
              <h2>{t("scorm_completion_all_done")}</h2>
              <p>{t("scorm_completion_all_done_description")}</p>
              <GenericButton variant="outlined" onClick={handleViewAllCourses}>
                {t("scorm_completion_view_all_courses")}
              </GenericButton>
            </div>
          )}
        </section>
      </section>
    </main>
  );
}
